import React, { useCallback, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Sheet, WalletOption } from '@pkg/components';
import { type Account } from '@pkg/graphql/codegen/graphql';
import { useAppSelector, useConnectAnyWallet } from '@pkg/hooks';
import { type SupportedWalletType } from '@pkg/types';

import { useWalletStore } from '@app/store/zustand';

import { urls, walletOptions } from '../../ConnectWalletSheet.data';
import { SupportedChains } from '../SupportedChains';

import {
    HeaderContent,
    Item,
    List,
    Section,
    SectionAction,
    SectionHeader,
    SectionTitle,
    Subtitle,
    Title,
} from './MainOverview.style';

export interface MainOverviewProps {
    onDisconnect?: (wallet: string) => void;
}

const MAX_INITIAL_CONNECTED_WALLETS = 3;

function getTypeFromAccount(accountAddress: Account['address']): SupportedWalletType {
    const [chain, _address] = accountAddress?.split('.') || [];
    switch (chain) {
        case 'aptos': {
            return 'petra';
        }
        case 'polygon': {
            // TODO: this is temp - we need to find a get the wallet connection type from account some how
            return 'walletConnect';
        }
        default:
        case 'ethereum': {
            return 'metaMask';
        }
    }
}
export function MainOverview({ onDisconnect }: MainOverviewProps) {
    const [translate] = useTranslation('connectWallet');
    const navigate = useNavigate();

    const { toggleShowOverview, connectedWallets } = useWalletStore();
    const { connect } = useConnectAnyWallet();

    const enablePetraWallet = useAppSelector((state) => state.featureFlags.features.enablePetraWallet);

    const denyList: SupportedWalletType[] = [];
    if (!enablePetraWallet) denyList.push('petra');

    const handleNewConnection = useCallback(
        async (type: SupportedWalletType) => {
            await connect(type);
        },
        [connect],
    );

    // TODO: Need a way to disable each of these.
    // Might want a `highlight` prop that filters by `supportedCurrencies`.
    // If `highlight`, show a `Create now` CTA.
    const filteredOptions = walletOptions.filter((option) => !denyList.includes(option));

    const optionItems = filteredOptions.map((type) => (
        <Item key={`OptionItem-${type}`}>
            <WalletOption type={type} onAction={() => handleNewConnection(type)} />
        </Item>
    ));

    // TODO: We may want to select wallets by some specific criteria.
    const connectedItems = useMemo(() => {
        if (connectedWallets?.length) {
            return connectedWallets.slice(0, MAX_INITIAL_CONNECTED_WALLETS).map((account) => (
                <Item key={`ConnectedWallet-${account?.address}`}>
                    <WalletOption
                        type={getTypeFromAccount(account?.address)} // TODO: We need to figure out how to get the wallet `type` from the `account` for Eth/Matic.
                        account={account}
                        onDisconnect={onDisconnect ? () => onDisconnect(account.address) : undefined}
                    />
                </Item>
            ));
        }
    }, [connectedWallets, onDisconnect]);

    function handleViewAll() {
        toggleShowOverview();
        navigate(urls.connectWallet);
    }

    const viewAllAction =
        connectedWallets && connectedWallets?.length > MAX_INITIAL_CONNECTED_WALLETS ? (
            <SectionAction aria-label={translate('sheet-view-all-cta-a11y')} onClick={handleViewAll}>
                {translate('sheet-view-all-cta')}
            </SectionAction>
        ) : null;

    const connectedSection = connectedItems?.length ? (
        <Section>
            <SectionHeader>
                <SectionTitle>{translate('sheet-section-connected-title')}</SectionTitle>

                {viewAllAction}
            </SectionHeader>

            <List>{connectedItems}</List>
        </Section>
    ) : null;

    return (
        <Sheet.Main>
            <Sheet.Main.SoloSection gutters padded>
                <HeaderContent>
                    <Title>{translate('sheet-connect-title')}</Title>
                    <Subtitle>{translate('sheet-connect-subtitle')}</Subtitle>
                </HeaderContent>

                <Section>
                    <List>{optionItems}</List>
                </Section>

                {connectedSection}

                <SupportedChains />
            </Sheet.Main.SoloSection>
        </Sheet.Main>
    );
}
